import type {
  Breadcrumb,
  Event,
  Integration,
  Span,
  SpanJSON,
} from '@sentry/core';

/**
 * Keeps credentials and search terms out of the URLs the browser SDK sends,
 * while leaving support enough to find the applicant an event is about.
 *
 * The SDK copies `window.location.href` onto every event, names pageload and
 * navigation transactions after the path, and records request URLs on
 * breadcrumbs and spans. On the applicant portal the path is the credential:
 * `/apply/:accountSlug/applications/:applicantExternalId` opens the portal, I-9
 * documents included, for whoever holds it. Query strings carry recruiter
 * search terms, which are candidate names, and any token a link was sent with.
 *
 * A credential id in a URL keeps its first 12 hex characters. The ids are
 * UUIDv4 (`uuid_generate_v4()` in hire), whose version nibble is the 13th hex
 * character, so those 12 are random: 48 bits that pin one applicant within an
 * account, and 74 unknown random bits that leave the URL useless as a key.
 * Support finds the applicant with
 * `WHERE external_id::text LIKE '6c1e0d5e-0000%'` scoped to the account slug
 * the URL keeps, or searches Sentry for `applicant.ref:6c1e0d5e0000`.
 */

const FILTERED = '[Filtered]';
const REF_LENGTH = 12;
const UUID =
  /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;

// The tag every event on the applicant portal carries.
export const APPLICANT_REF_TAG = 'applicant.ref';

type Segment = { pattern: RegExp; placeholder: string };

// Each segment authenticates on its own. `:rest` of `remoteI9Authorization` is
// the I-9 form's external id, a UUIDv4 like the applicant's and a credential
// for the remote authorization page, so it gets the same treatment.
const CREDENTIAL_SEGMENTS: readonly Segment[] = [
  {
    pattern: /(\/applications\/)([^/?#\s]+)/g,
    placeholder: ':applicantExternalId',
  },
  {
    pattern: /(\/remoteI9Authorization\/)([^/?#\s]+)(?:\/[^?#\s]*)?/g,
    placeholder: ':i9FormExternalId',
  },
  // The remote I-9 page's own API calls carry the same id.
  {
    pattern: /(\/authorized_representative_forms\/)([^/?#\s]+)/g,
    placeholder: ':i9FormExternalId',
  },
  { pattern: /(\/i9_forms\/)([^/?#\s]+)/g, placeholder: ':i9FormExternalId' },
];

const APPLICANT_SEGMENT = /\/applications\/([^/?#\s]+)/;
const QUERY = /\?([^#\s]*)/g;
// Only a fragment shaped like parameters: `#root` in a CSS selector stays.
const PARAMETER_FRAGMENT = /#([^#\s]*=[^#\s]*)/g;

// An id already cut to its ref. A breadcrumb is scrubbed when it is recorded
// and again with the event that carries it.
const SCRUBBED_REF = /^([0-9a-f]{12})…$/i;

/** The first 12 hex characters of a UUID, or nothing for any other shape. */
const refOf = (id: string): string | undefined => {
  if (UUID.test(id)) {
    return id.replace(/-/g, '').slice(0, REF_LENGTH).toLowerCase();
  }
  return SCRUBBED_REF.exec(id)?.[1].toLowerCase();
};

// A bare segment with no `=` may be the token itself, so it goes whole.
const redactParameters = (parameters: string): string =>
  parameters
    .split('&')
    .map(parameter => {
      const equals = parameter.indexOf('=');
      if (equals === -1) {
        return parameter && FILTERED;
      }
      return equals === parameter.length - 1
        ? parameter
        : `${parameter.slice(0, equals)}=${FILTERED}`;
    })
    .join('&');

const scrubCredentials = (value: string, keepRef: boolean): string =>
  CREDENTIAL_SEGMENTS.reduce(
    (scrubbed, { pattern, placeholder }) =>
      scrubbed.replace(pattern, (_, prefix: string, id: string) => {
        const ref = keepRef ? refOf(id) : undefined;
        return `${prefix}${ref ? `${ref}…` : placeholder}`;
      }),
    value,
  );

const scrub = (value: string, keepRef: boolean): string =>
  scrubCredentials(value, keepRef)
    .replace(QUERY, (_, query: string) => `?${redactParameters(query)}`)
    .replace(
      PARAMETER_FRAGMENT,
      (_, fragment: string) => `#${redactParameters(fragment)}`,
    );

/**
 * For a URL an event records: a credential id keeps its 12-character ref,
 * `/applications/6c1e0d5e0000…`, and every query-string value goes.
 */
export const scrubUrl = (value: string): string => scrub(value, true);

/**
 * For a transaction name or span description: a credential id becomes its
 * route parameter, `/applications/:applicantExternalId`. A per-applicant value
 * there would make every applicant a transaction group of their own.
 */
export const parameterizeUrl = (value: string): string => scrub(value, false);

/** The `applicant.ref` of a portal URL, if it names an applicant. */
export const applicantRef = (url: string): string | undefined => {
  const match = APPLICANT_SEGMENT.exec(url);
  return match ? refOf(match[1]) : undefined;
};

// Mutates in place: the SDK hands over objects it owns and reads them back.
const scrubValues = (
  draftRecord: Record<string, unknown> | undefined,
): void => {
  if (!draftRecord) {
    return;
  }
  Object.keys(draftRecord).forEach(key => {
    const value = draftRecord[key];
    if (typeof value === 'string') {
      draftRecord[key] = scrubUrl(value);
    }
  });
};

// The free-text attributes the SDK copies into a click's CSS selector. A
// component that puts a candidate's name in `aria-label` would otherwise send
// it with every click.
const FREE_TEXT_ATTRIBUTE = /\[(aria-label|title|alt)="/g;
// The SDK does not escape a quote inside a value, so a value ends at the first
// `"]` that a selector part or the end of the string follows.
const VALUE_END = /"\](?=[[.#\s]|$)/g;

/**
 * Keeps the attribute's presence in a selector and drops its text. A value
 * with no recognizable end is dropped with everything after it.
 */
export const scrubSelector = (selector: string): string => {
  let scrubbed = '';
  let from = 0;
  FREE_TEXT_ATTRIBUTE.lastIndex = 0;
  for (
    let start = FREE_TEXT_ATTRIBUTE.exec(selector);
    start;
    start = FREE_TEXT_ATTRIBUTE.exec(selector)
  ) {
    VALUE_END.lastIndex = FREE_TEXT_ATTRIBUTE.lastIndex;
    const end = VALUE_END.exec(selector);
    scrubbed += `${selector.slice(from, start.index)}[${start[1]}="${FILTERED}"]`;
    if (!end) {
      return scrubbed;
    }
    from = VALUE_END.lastIndex;
    FREE_TEXT_ATTRIBUTE.lastIndex = from;
  }
  return scrubbed + selector.slice(from);
};

/**
 * `beforeBreadcrumb`. Runs before a breadcrumb reaches the scope and before
 * session replay records it, so neither holds the raw URL.
 *
 * Console breadcrumbs go whole. Their arguments are whatever the app logged,
 * an `ApiError` among them, whose `body` holds what a candidate typed into a
 * form. Dropping them here rather than leaving out the SDK's `Breadcrumbs`
 * console option keeps working if the SDK renames the option.
 */
export function scrubBreadcrumb(
  draftBreadcrumb: Breadcrumb,
): Breadcrumb | null {
  if (draftBreadcrumb.category === 'console') {
    return null;
  }
  if (draftBreadcrumb.message) {
    const message = draftBreadcrumb.category?.startsWith('ui.')
      ? scrubSelector(draftBreadcrumb.message)
      : draftBreadcrumb.message;
    draftBreadcrumb.message = scrubUrl(message);
  }
  scrubValues(draftBreadcrumb.data);
  return draftBreadcrumb;
}

type RecordingPayload = {
  category?: unknown;
  description?: unknown;
  message?: unknown;
  data?: Record<string, unknown> & {
    node?: { attributes?: Record<string, unknown> };
  };
};

const FREE_TEXT_ATTRIBUTES = ['aria-label', 'title', 'alt'];

/**
 * `beforeAddRecordingEvent`. Scrubs the navigation, network and breadcrumb
 * entries a replay records alongside the DOM.
 *
 * It does not make a replay safe for a page whose own URL is a credential. The
 * SDK hands this hook custom events only; the rrweb meta event that opens every
 * full snapshot carries `window.location.href` and reaches no hook, and the DOM
 * snapshot records the `href` of every link `replayPrivacyOptions` does not
 * block.
 *
 * A click the replay records itself carries the clicked element's
 * `aria-label`, `title` and `alt`, unmasked on an element tagged
 * `.sentry-unmask`. Those go, as they do from a click breadcrumb.
 */
export function scrubRecordingEvent<T>(draftEvent: T): T | null {
  const payload = (draftEvent as { data?: { payload?: RecordingPayload } }).data
    ?.payload;
  if (!payload) {
    return draftEvent;
  }
  if (payload.category === 'console') {
    return null;
  }
  const isUi =
    typeof payload.category === 'string' && payload.category.startsWith('ui.');
  if (typeof payload.description === 'string') {
    payload.description = scrubUrl(payload.description);
  }
  if (typeof payload.message === 'string') {
    payload.message = scrubUrl(
      isUi ? scrubSelector(payload.message) : payload.message,
    );
  }
  const attributes = payload.data?.node?.attributes;
  if (attributes) {
    FREE_TEXT_ATTRIBUTES.filter(name => name in attributes).forEach(name => {
      attributes[name] = FILTERED;
    });
  }
  scrubValues(payload.data);
  return draftEvent;
}

/**
 * The masking every frontend passes to `replayIntegration`, next to
 * `beforeAddRecordingEvent: scrubRecordingEvent`.
 *
 * Text, inputs and media stay masked, and a component opts its own subtree out
 * with `.sentry-unmask`. The alternative, `maskAllText: false` with a list of
 * sensitive selectors, publishes every element nobody has labelled.
 *
 * Nothing is unmasked by element type. `unmask` reaches the whole subtree
 * under a match and keeps its `aria-label` and `title`, and no tag or role
 * holds only chrome: a `nav` holds the impersonation chooser's customer name,
 * and an MUI `ListItem button` renders a `div[role="button"]` around an
 * applicant note and its author. Chrome that should read in a replay carries
 * `.sentry-unmask` itself.
 *
 * Links and frames whose URL can carry a credential or a contact detail are
 * blocked, not masked. rrweb resolves every `href` and `src` to an absolute URL
 * and returns before the SDK's attribute masking runs, so `maskAttributes`
 * never sees one, and `mask` covers text only. A blocked element is recorded
 * as an empty box of the same size: no attributes, no children.
 *
 * Blocked:
 * - contact links, `tel:`, `mailto:` and `sms:`;
 * - any link with a query string: a document download is a presigned URL that
 *   carries its decryption key and opens the file for a day, and query values
 *   are where every other token and search term in a link lives;
 * - any link through a credential path segment, an applicant's portal among
 *   them, and any `download`, `blob:` or `data:` link;
 * - every `iframe`, whose `src` rrweb keeps as `rr_src` when the frame is
 *   cross-origin: offer-letter previews and signed analytics embeds sit in one.
 *
 * The cost is that a navigation link with a query string reads as a blank box.
 * Other links keep their `href`, a page URL with no query, the same limit as
 * the page URL itself. Media is blocked by `blockAllMedia`.
 */
export const replayPrivacyOptions = {
  maskAllText: true,
  maskAllInputs: true,
  blockAllMedia: true,
  block: [
    'a[href^="tel:" i]',
    'a[href^="mailto:" i]',
    'a[href^="sms:" i]',
    'a[href*="?"]',
    'a[href*="/applications/"]',
    'a[href*="/remoteI9Authorization/"]',
    'a[href*="/authorized_representative_forms/"]',
    'a[href*="/i9_forms/"]',
    'a[download]',
    'a[href^="blob:" i]',
    'a[href^="data:" i]',
    'iframe',
  ],
  unmask: ['.sentry-unmask', '[data-sentry-unmask]'],
};

// A bundle's own URL, the key its source map is found under.
// Anchored on the path, so a query value that ends in `.js` does not count.
const SCRIPT_ASSET = /^[^?#\s]*\.[cm]?js(?:[?#]|$)/;

/**
 * For a stack frame's file. The page URL, query included, becomes the frame's
 * file when a script inline in the page throws, an account's tracker snippet on
 * the portal among them, and when the browser reports an error with no stack
 * or file. A bundle's URL keeps its query and fragment, which source-map lookup
 * matches on, and loses only a credential segment.
 */
const scrubFrameUrl = (value: string): string =>
  SCRIPT_ASSET.test(value) ? scrubCredentials(value, true) : scrubUrl(value);

// Span attributes that hold a CSS selector: the element that painted the
// largest contentful paint, and each element that shifted the layout.
const SELECTOR_ATTRIBUTE = /^(?:lcp\.element|cls\.source\.\d+)$/;

const scrubSpanData = (
  draftData: Record<string, unknown> | undefined,
): void => {
  if (!draftData) {
    return;
  }
  Object.keys(draftData).forEach(key => {
    const value = draftData[key];
    if (typeof value !== 'string') {
      return;
    }
    if (key === 'transaction') {
      draftData[key] = parameterizeUrl(value);
    } else {
      draftData[key] = scrubUrl(
        SELECTOR_ATTRIBUTE.test(key) ? scrubSelector(value) : value,
      );
    }
  });
};

// An interaction span is named after the clicked element's selector.
const scrubSpanDescription = (description: string): string =>
  parameterizeUrl(scrubSelector(description));

/**
 * `beforeSendSpan`. Reaches every span the SDK sends, whatever its op: those
 * inside a transaction, and the standalone ones no event processor sees. An
 * INP span is standalone, named after the clicked element with its
 * `aria-label`, and carries the pageload's raw path as `transaction`.
 */
export function scrubSpan(draftSpan: SpanJSON): SpanJSON {
  if (draftSpan.description) {
    draftSpan.description = scrubSpanDescription(draftSpan.description);
  }
  scrubSpanData(draftSpan.data);
  return draftSpan;
}

/**
 * Sample rates come from the environment so a flood can be shed on the next
 * build rather than in a code change. The bundler inlines them.
 */
export const sampleRate = (
  raw: string | undefined | null,
  fallback: number,
): number => {
  // An unset variable arrives as null from EnvironmentPlugin, and as "" from
  // Vite or a build that pins the name to "". `Number` reads both as 0, which
  // would turn the signal off without saying so.
  if (raw === undefined || raw === null || raw === '') {
    return fallback;
  }

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// `traceFlags` bit 0, as in W3C trace context.
const TRACE_FLAG_SAMPLED = 1;

const escapeForRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * `tracePropagationTargets`: same-origin requests, plus requests to the
 * monolith this app calls while the trace they run in is sampled.
 *
 * A browser span joins the server span it caused only when `sentry-trace` and
 * `baggage` ride along on the request. Neither header is CORS-safelisted, so a
 * cross-origin call carrying them is preflighted first —
 * `apps/hire/config/initializers/cors.rb` declares `headers: :any` on every
 * resource these frontends call, rack-cors short-circuits on `:any` and echoes
 * the requested headers back, and the preflight passes.
 *
 * The monolith target matches only while a sampled pageload or navigation
 * span is open, which ends a second after the last request it saw and at 30
 * seconds at most. Everywhere else a GET to the monolith stays a simple
 * request, with no preflight, and carries no sampling decision: the SDK would
 * send `sampled=0` from an unsampled root span, and a server SDK obeys a
 * parent's decision over its own `traces_sample_rate`, so the browser's rate
 * would become hire's. The cost is that only the requests a sampled pageload
 * or navigation makes join a server trace.
 *
 * The SDK tests each target with its own `test`, so the monolith target is a
 * RegExp whose `test` also checks the active span. It is anchored on the origin
 * and closed by `/`, never the bare origin string: Sentry matches a string
 * target as a substring anywhere in the URL, so `https://web.fountain.com`
 * would also match `https://attacker.example/?next=https://web.fountain.com`
 * and hand that host the trace id and its dynamic sampling context. The closing
 * `/` keeps a `web.fountain.com.attacker.example` lookalike out.
 *
 * An empty origin means the monolith is proxied onto this app's own origin, so
 * no second target is added: `/^\//` already covers it.
 */
export const monolithTracePropagationTargets = (
  monolithOrigin: string,
  getActiveSpan: () => Span | undefined,
): RegExp[] => {
  if (!monolithOrigin) {
    return [/^\//];
  }

  const monolith = new RegExp(`^${escapeForRegExp(monolithOrigin)}/`);
  const matchesMonolith = RegExp.prototype.test.bind(monolith);
  monolith.test = (url: string): boolean =>
    getActiveSpan()?.spanContext().traceFlags === TRACE_FLAG_SAMPLED &&
    matchesMonolith(url);

  return [/^\//, monolith];
};

type AnyEvent = Event & { urls?: string[] };

const scrubEvent = (draftEvent: AnyEvent): AnyEvent => {
  // Read before the URL is scrubbed. The page URL is where the portal learns
  // the applicant, so every event made on it, a transaction or a replay
  // included, carries the ref from the moment the id is in the address bar.
  const ref = draftEvent.request?.url && applicantRef(draftEvent.request.url);
  if (ref && !draftEvent.tags?.[APPLICANT_REF_TAG]) {
    draftEvent.tags = { ...draftEvent.tags, [APPLICANT_REF_TAG]: ref };
  }

  if (draftEvent.request) {
    if (draftEvent.request.url) {
      draftEvent.request.url = scrubUrl(draftEvent.request.url);
    }
    const { headers } = draftEvent.request;
    if (headers) {
      delete headers.Cookie;
      delete headers.cookie;
    }
    // `Referer` is the page the visitor came from, often another portal URL.
    scrubValues(headers);
    delete draftEvent.request.cookies;
    delete draftEvent.request.data;
  }

  // A rejection with a plain object, a response body say, arrives serialized
  // here. The message still names its keys.
  if (draftEvent.extra) {
    delete draftEvent.extra.__serialized__;
  }

  // A message can quote the URL that failed. Only the credential segments go:
  // query redaction would rewrite a message's `?` and split its issue group.
  draftEvent.exception?.values?.forEach(draftValue => {
    if (draftValue.value) {
      draftValue.value = scrubCredentials(draftValue.value, false);
    }
    draftValue.stacktrace?.frames?.forEach(draftFrame => {
      if (draftFrame.filename) {
        draftFrame.filename = scrubFrameUrl(draftFrame.filename);
      }
      if (draftFrame.abs_path) {
        draftFrame.abs_path = scrubFrameUrl(draftFrame.abs_path);
      }
    });
  });

  if (draftEvent.transaction) {
    draftEvent.transaction = parameterizeUrl(draftEvent.transaction);
  }

  scrubSpanData(draftEvent.contexts?.trace?.data);

  const feedback = draftEvent.contexts?.feedback;
  if (typeof feedback?.url === 'string') {
    feedback.url = scrubUrl(feedback.url);
  }

  draftEvent.spans?.forEach(scrubSpan);

  if (draftEvent.breadcrumbs) {
    draftEvent.breadcrumbs = draftEvent.breadcrumbs.filter(
      breadcrumb => scrubBreadcrumb(breadcrumb) !== null,
    );
  }

  if (draftEvent.urls) {
    draftEvent.urls = draftEvent.urls.map(scrubUrl);
  }

  return draftEvent;
};

/**
 * Scrubs every event type on its way out: errors, transactions and their
 * spans, feedback and replay events. `beforeSend` sees errors only and
 * `beforeSendTransaction` transactions only; an event processor sees all four,
 * after the SDK has attached the page URL.
 *
 * Besides URLs, it drops the carriers of free text a request or response can
 * put on an event: cookies, a request body, a serialized rejection, and
 * console breadcrumbs.
 */
export const scrubUrlsIntegration = (): Integration => ({
  name: 'ScrubUrls',
  processEvent: scrubEvent,
});
